import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../../shared/components/ui/badge/badge.component';
import { TaskCatalogApiService } from '../../../services/task-catalog-api.service';
import { TaskTypeDrawerService } from '../../../services/task-type-drawer.service';
import { TaskTypeResponse } from '../../../models/task.model';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { PermissionService } from '../../../../../shared/services/permission.service';

@Component({
  selector: 'app-task-types-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './task-types-list.component.html',
  styleUrls: ['./task-types-list.component.css'],
})
export class TaskTypesListComponent implements OnInit, OnDestroy {
  items: TaskTypeResponse[] = [];
  loading = false;
  error = '';
  /** Reorden en vuelo: evita drags encadenados mientras el POST no confirma. */
  reordering = false;

  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';

  /**
   * La pantalla no pagina: es un catálogo de config de unas pocas filas y se carga entero
   * (como los ciclos de lead) para que el arrastre esté siempre disponible y el registro
   * recién creado sea siempre visible.
   */
  private readonly CATALOG_PAGE_SIZE = 100;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: TaskCatalogApiService,
    private drawerService: TaskTypeDrawerService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private permissions: PermissionService,
  ) {}

  /** POST /api/task-types/reorder exige tasks:EDIT (mismo permiso que el botón Editar). */
  get puedeEditar(): boolean {
    return this.permissions.has('tasks', 'edit');
  }

  /**
   * El endpoint de reorden renumera 1..N con los ids que recibe, así que enviar una lista
   * parcial descolocaría a los registros omitidos. La carga ya trae el catálogo completo,
   * pero los filtros SÍ recortan lo que se ve: con un filtro activo `items` es parcial y
   * arrastrar renumeraría solo esos ids. Por eso el arrastre exige que no haya filtros.
   */
  get listaCompletaVisible(): boolean {
    return !this.filtroSearch.trim() && this.filtroActive === '';
  }

  get canReorder(): boolean {
    return this.puedeEditar && this.listaCompletaVisible;
  }

  ngOnInit(): void {
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    const isActive =
      this.filtroActive === '' ? undefined : this.filtroActive === 'true';
    this.api.listTypes(0, this.CATALOG_PAGE_SIZE, isActive).subscribe({
      next: (page) => {
        let data = [...page.content];
        if (this.filtroSearch.trim()) {
          const term = this.filtroSearch.trim().toLowerCase();
          data = data.filter(
            (i) =>
              i.name.toLowerCase().includes(term) ||
              (i.label || '').toLowerCase().includes(term),
          );
        }
        this.items = data.sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
        );
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando tipos de tarea';
        this.loading = false;
      },
    });
  }

  buscar(): void {
    this.cargar();
  }

  /**
   * Reordena por drag & drop. Solo se llega aquí sin filtros (ver `listaCompletaVisible`)
   * y la carga no pagina, así que `items` es la lista completa del negocio.
   *
   * Estrategia optimista: se mueve la fila y se renumera la columna "Orden" (1..N) al instante;
   * si la API falla se revierte el orden y el displayOrder exactos que había antes.
   * Sin toast en el éxito (regla de drag-drop del skill crm-toast-notifications).
   */
  dropTipo(event: CdkDragDrop<TaskTypeResponse[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    if (!this.canReorder || this.reordering) return;

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) return;

    // Snapshot para revertir: conserva posición Y displayOrder previos de cada ítem.
    const snapshot = this.items.map(i => ({ item: i, displayOrder: i.displayOrder }));

    moveItemInArray(this.items, event.previousIndex, event.currentIndex);
    this.items.forEach((i, idx) => i.displayOrder = idx + 1);

    const orderedIds = this.items.map(i => i.id);

    this.reordering = true;
    this.api.reorderTypes(businessId, orderedIds).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.reordering = false;
      },
      error: (err) => {
        this.reordering = false;
        // Revertir: la UI no puede quedar mostrando un orden que el backend rechazó.
        this.items = snapshot.map(s => {
          s.item.displayOrder = s.displayOrder;
          return s.item;
        });
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al reordenar los tipos de tarea'
        });
      }
    });
  }

  nuevo(): void {
    this.drawerService.open();
  }
  editar(item: TaskTypeResponse): void {
    this.drawerService.open({ typeId: item.id });
  }

  toggle(item: TaskTypeResponse): void {
    this.api
      .updateType(item.id, {
        name: item.name,
        label: item.label,
        icon: item.icon,
        color: item.color,
        displayOrder: item.displayOrder,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.cargar() });
  }

  async eliminar(item: TaskTypeResponse): Promise<void> {
    if (item.isActive) {
      await this.confirmDialog.alert({
        title: 'Aviso',
        message:
          'No se puede eliminar un tipo protegido del sistema. Puedes inhabilitarlo con el botón de activar/desactivar.',
        variant: 'warning',
      });
      return;
    }
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar tipo',
      message: `Eliminar el tipo "${item.label}"? Esta acción es permanente. Si hay tareas usándolo, no podrá eliminarse (reasígnalas o inhabilítalo).`,
    });
    if (!ok) return;
    this.api.deleteType(item.id).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted('Tipo de tarea', `Se eliminó "${item.label || item.name}" correctamente.`);
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar',
        });
      },
    });
  }

  trackById = (_: number, item: TaskTypeResponse) => item.id;
}
